import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatErr } from "../../../../lib/api";
import { Input } from "../../../../components/ui/input";
import { Button } from "../../../../components/ui/button";
import { Pencil } from "lucide-react";
import { toast } from "sonner";
import { DetailField, SectionCard, StatusPill } from "./shared";
import { ACCOUNTING_BASE } from "./accountingNav";

const ROOT_TYPE_LABELS = { ASSET: "Assets", LIABILITY: "Liabilities", FUND: "Funds / Capital", INCOME: "Income", EXPENSE: "Expenses" };

/**
 * Combined View + inline Edit page for a single Account Group
 * (/chart-of-accounts/:groupId). A group only has two editable fields
 * (name, active) plus an immutable rootType, so a single page covers View,
 * Edit and Deactivate without needing three separate routes for two fields.
 * System groups (isSystem) are always read-only, for anyone, at any
 * permission level - the backend enforces this independently.
 */
export default function AccountGroupViewPage() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await api.get(`/society/accounting/engine/account-groups/${groupId}`);
      setGroup(res.data);
      setName(res.data.name);
    } catch (err) {
      toast.error(formatErr(err, "Could not load this account group"));
      navigate(`${ACCOUNTING_BASE}/chart-of-accounts`, { replace: true });
    }
  };

  useEffect(() => { load(); }, [groupId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!name.trim()) { toast.error("Group name is required"); return; }
    try {
      setBusy(true);
      await api.patch(`/society/accounting/engine/account-groups/${groupId}`, { name });
      toast.success("Group updated");
      setEditing(false);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not update group"));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async () => {
    try {
      setBusy(true);
      await api.patch(`/society/accounting/engine/account-groups/${groupId}`, { active: group.active === false });
      toast.success(group.active === false ? "Group reactivated" : "Group deactivated - it can no longer accept new ledgers");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not update group status"));
    } finally {
      setBusy(false);
    }
  };

  if (!group) return <div className="text-sm text-[#6B7280]">Loading...</div>;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <Link to={`${ACCOUNTING_BASE}/chart-of-accounts`} className="text-xs font-semibold uppercase tracking-[0.16em] text-[#6B7280] hover:text-[#111827]">
            &larr; Chart of Accounts
          </Link>
          <h2 className="mt-1 text-lg font-extrabold text-[#111827]">
            {group.name}
            {group.isSystem && <span className="ml-2 rounded-sm bg-[#F3F4F6] px-2 py-0.5 text-xs font-semibold uppercase text-[#6B7280]">System group</span>}
          </h2>
        </div>
        {!group.isSystem && !editing && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={toggleActive} disabled={busy}>{group.active === false ? "Reactivate" : "Deactivate"}</Button>
            <Button onClick={() => setEditing(true)} className="bg-[#111827] text-white hover:bg-[#374151]"><Pencil className="mr-2 h-4 w-4" /> Edit</Button>
          </div>
        )}
      </div>

      <SectionCard title="Group details">
        {editing ? (
          <div className="max-w-sm space-y-3">
            <div>
              <label className="text-[11px] uppercase tracking-[0.16em] text-[#6B7280]">Group name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="flex gap-2">
              <Button disabled={busy} onClick={save} className="bg-[#111827] text-white hover:bg-[#374151]">Save changes</Button>
              <Button type="button" variant="outline" disabled={busy} onClick={() => { setEditing(false); setName(group.name); }}>Cancel</Button>
            </div>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <DetailField label="Root type (fixed)" value={ROOT_TYPE_LABELS[group.rootType] || group.rootType} />
            <DetailField label="Ledgers in this group" value={group.ledgerCount} />
            <div>
              <div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Status</div>
              <div className="mt-1"><StatusPill active={group.active !== false} /></div>
            </div>
          </div>
        )}
        <p className="mt-4 text-xs text-[#6B7280]">Root type can never be changed after creation - it would silently reclassify every ledger already filed under this group. Deactivating a group blocks new ledgers from being added to it; existing ledgers and their history are unaffected.</p>
      </SectionCard>
    </div>
  );
}
